import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { DateRangeType } from '@g17eco/types/common';
import { TIME_RANGE_YEARLY, getSurveyDateRange } from '@utils/date';
import { saveSharedDateRange, loadSharedDateRange } from '@utils/portfolioFilterStorage';

interface BenchmarkingDateRangeContextType {
  dateRange: DateRangeType;
  timeRange: string | number;
  setDateTimeRange: (dateRange: DateRangeType, timeRange: string | number) => void;
}

const BenchmarkingDateRangeContext = createContext<BenchmarkingDateRangeContextType | undefined>(undefined);

interface BenchmarkingDateRangeProviderProps {
  children: ReactNode;
  portfolioId: string;
}

export const BenchmarkingDateRangeProvider = ({ children, portfolioId }: BenchmarkingDateRangeProviderProps) => {
  // Initialize from sessionStorage
  const [state, setState] = useState<{ dateRange: DateRangeType; timeRange: string | number }>(() => {
    const saved = loadSharedDateRange(portfolioId);
    return {
      dateRange: saved?.dateRange || getSurveyDateRange(TIME_RANGE_YEARLY),
      timeRange: saved?.timeRange || TIME_RANGE_YEARLY,
    };
  });

  const { dateRange, timeRange } = state;

  // Reload from sessionStorage when portfolioId changes
  useEffect(() => {
    const saved = loadSharedDateRange(portfolioId);
    if (saved) {
      setState({ dateRange: saved.dateRange, timeRange: saved.timeRange });
    } else {
      setState({ dateRange: getSurveyDateRange(TIME_RANGE_YEARLY), timeRange: TIME_RANGE_YEARLY });
    }
  }, [portfolioId]);

  // Persist to sessionStorage whenever state changes
  useEffect(() => {
    if (timeRange !== undefined) {
      saveSharedDateRange(portfolioId, { dateRange, timeRange });
    }
  }, [portfolioId, dateRange, timeRange]);

  const setDateTimeRange = (newDateRange: DateRangeType, newTimeRange: string | number) => {
    setState({ dateRange: newDateRange, timeRange: newTimeRange });
  };

  return (
    <BenchmarkingDateRangeContext.Provider value={{ dateRange, timeRange, setDateTimeRange }}>
      {children}
    </BenchmarkingDateRangeContext.Provider>
  );
};

export const useBenchmarkingDateRange = () => {
  const context = useContext(BenchmarkingDateRangeContext);
  if (context === undefined) {
    throw new Error('useBenchmarkingDateRange must be used within a BenchmarkingDateRangeProvider');
  }
  return context;
};
